
import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../../../utils/config';
import { useAuth } from '../../../../context/AuthContext';
import './QuoteAttachmentsModal.scss';

interface QuoteAttachmentsModalProps {
    isOpen: boolean;
    onClose: () => void;
    requestId: string;
    supplierId: string;
    supplierName: string;
}

interface Attachment {
    attachment_id: string; // or id
    name?: string;
    filename?: string;
    url?: string;
    content_type?: string;
    size?: number;
    // Helper fields added during processing
    messageSubject?: string;
    messageDate?: string;
}

const QuoteAttachmentsModal: React.FC<QuoteAttachmentsModalProps> = ({
    isOpen,
    onClose,
    requestId,
    supplierId,
    supplierName
}) => {
    const { token } = useAuth();
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Load messages and extract attachments on open
    useEffect(() => {
        if (isOpen && requestId && supplierId) {
            loadAttachments();
        }
    }, [isOpen, requestId, supplierId]);

    const loadAttachments = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(API_ENDPOINTS.REQUEST_COMMUNICATION_MESSAGES(requestId, supplierId), {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const result = await response.json();

            if (!result.success) {
                throw new Error(result.error || 'Failed to load messages');
            }

            const messages = result.data?.messages || [];
            const allAttachments: Attachment[] = [];

            messages.forEach((msg: any) => {
                // Filter for messages received FROM the supplier
                if (msg.direction === 'received' && msg.attachments && msg.attachments.length > 0) {
                    msg.attachments.forEach((att: any) => {
                        // Normalize attachment object
                        allAttachments.push({
                            ...att,
                            attachment_id: att.attachment_id || att.id,
                            messageSubject: msg.subject,
                            messageDate: msg.sent_at
                        });
                    });
                }
            });

            setAttachments(allAttachments);

        } catch (err: any) {
            console.error('Error loading attachments:', err);
            setError(err.message || 'Error occurred while loading attachments.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = (att: Attachment) => {
        const attachmentId = att.attachment_id;
        if (!attachmentId || !requestId) return;

        const downloadUrl = att.url && att.url.startsWith('/api/v2/requests')
            ? API_ENDPOINTS.REQUEST_COMMUNICATION_ATTACHMENT(requestId, attachmentId)
            : `${API_ENDPOINTS.BASE_URL}/mailgun/attachments/${attachmentId}`;

        window.open(`${downloadUrl}?token=${token}`, '_blank');
    };

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return '';
        try {
            return new Date(dateStr).toLocaleDateString('tr-TR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateStr;
        }
    };

    if (!isOpen) return null;

    return (
        <div className="quote-attachments-modal-overlay" onClick={onClose}>
            <div className="quote-attachments-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Attachments: {supplierName}</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                <div className="modal-body">
                    {isLoading ? (
                        <div className="loading">Loading attachments...</div>
                    ) : error ? (
                        <div className="error">{error}</div>
                    ) : attachments.length === 0 ? (
                        <div className="empty">
                            <span className="icon">📎</span>
                            <p>No attachments found from this supplier.</p>
                        </div>
                    ) : (
                        <div className="attachments-list">
                            {attachments.map((att, index) => (
                                <div key={`${att.attachment_id}-${index}`} className="attachment-item">
                                    <div className="file-info">
                                        <span className="file-name">{att.name || att.filename || 'Attachment'}</span>
                                        <span className="file-meta">
                                            {att.messageSubject ? `Re: ${att.messageSubject}` : 'No Subject'} • {formatDate(att.messageDate)}
                                        </span>
                                    </div>
                                    <button
                                        className="download-btn"
                                        onClick={() => handleDownload(att)}
                                    >
                                        Download
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <button className="btn-close" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default QuoteAttachmentsModal;
